'use strict';

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {each} from 'underscore';

import {Row, Col} from 'react-bootstrap';

import SliderRange from '@uw-it-sis/lib-react/lib/SliderRange';

/**
 * NOTE:
 *      This component isn't being used (it was in course search for a little while, but the user experience was problematic
 *      and we took it out). However, I'm leaving it in because the refactor to make non-binary facets work was pretty
 *      significant (i.e. hard to roll back) and I think it's a pretty good feature. This component provides a good example
 *      of how to implement a non-binary facet.
 *
 * UI facet filter component (aka one of the items in a facet that you can toggle)
 *
 * Assumptions ...
 *
 * o The earliest possible start time is 7:30am
 * o The latest end time is 9:30pm
 *
 * How it works ...
 *
 * The Slider component doesn't deal with time natively so we have to convert the start/end time ranges to minutes.
 *
 * A day has 1440 minutes. In this component minutes are represented as their distance from midnight/0. So, 7:30am
 * would be 450 and 9:30pm would be 1290.
 *
 * When the component mounts it will be initialized to the min and max minutes in the data set and the default/current
 * minutes will be the same values. As the slider gets slidden the new positions will be written to the state and rendered.
 * When the sliding stops a list of filter ids which fall within the current minutes range will be created and an
 * event fired with those Ids as the payload to have the corresponding filter states updated.
 *
 */
class TimeRangeFilter extends Component {

    constructor(props) {
        super(props);
        this.props = props;

        /* 7:30am in minutes */
        this.MIN_START_TIME = 450;
        /* 9:30pm in minutes */
        this.MAX_END_TIME = 1290;

        this.state = this._getStartAndEndTimes();

        this.onChange = this.onChange.bind(this);
        this.onAfterChange = this.onAfterChange.bind(this);
    }


    // _unsubscribeClear: undefined


    componentDidMount() {
        // Manually subscribe to the clear action and save the unsubscribe method for the unmount.
        // FIXME: this._unsubscribeClear = FacetActions.clear.listen(this._clear);
    }

    componentDidUnmount() {
        // Stop listening for clear events.
        // FIXME: this._unsubscribeClear();
    }

    // FIXME: This is an event handler to clear actions.
    _clear(facetId) {
        // If the clear method is fired for this action then reset the sliders to their start positions.
        if (this.props.facetModel.getId() === facetId) {
            // If the clean button has been clicked on the facet then reset the slider position.
            let msm = this.state.minStartMinutes;
            let mem = this.state.maxEndMinutes;
            this.setState({
                currentStartMinutes: msm,
                currentEndMinutes: mem
            });
        }
    }

    /**
     * Determines the range that the slider should display by iterating through the filter values
     * and finding the earliest start time and latest end time.
     */
    _getStartAndEndTimes() {
        // Init these to the min and max possible values.
        let minObservedStartminutes = undefined;
        let maxObservedEndMinutes = undefined;

        /*
         * Find the earliest start time
         * Find the number of minutes between the earliest start time and the latest end time.
         */
        let filterModels = this.props.facetModel.getFilterModels();

        // Not using 'each' here because sometimes we need to stop iterating and there's no way to do that with 'each'
        for (let i = 0; i < filterModels.length; i++) {
            let filter = filterModels[i];

            /*
             * If the filter key is not in the format 123:123 then consider it TBA (To Be Announced).
             *
             * If we don't factor TBAs in here by giving them the max range they would get filtered out and therefore
             * not be viewable in the search results at all.
             *
             * Also, once we hit a TBA we know that the values of minObservedStartminutes/maxObservedEndMinutes
             * isn't going to change again, so no further iteration is required.
             */
            if (!filter.key.includes(":")) {
                minObservedStartminutes = this.MIN_START_TIME;
                maxObservedEndMinutes = this.MAX_END_TIME;
                // The end
                break;
            }

            let timeTokens = filter.key.split(":");
            let startTime = timeTokens[0];
            let endTime = timeTokens[1];

            // Convert the raw times to minutes
            let startMinutes = this._timeToMinutes(startTime);
            let endMinutes = this._timeToMinutes(endTime);

            // Go ahead and set the min/max observed values if they aren't yet.
            if (minObservedStartminutes === undefined) {
                minObservedStartminutes = startMinutes;
            }
            if (maxObservedEndMinutes === undefined) {
                maxObservedEndMinutes = endMinutes;
            }

            // If there's a greater or lesser value update the observed values.
            if (startMinutes < minObservedStartminutes) {
                minObservedStartminutes = startMinutes;
            }
            if (endMinutes > maxObservedEndMinutes) {
                maxObservedEndMinutes = endMinutes;
            }
        }

        return ({
            minStartMinutes: minObservedStartminutes,
            maxEndMinutes: maxObservedEndMinutes,
            currentStartMinutes: minObservedStartminutes,
            currentEndMinutes: maxObservedEndMinutes
        });
    }

    /**
     * Iterates over the collection of filters and sums the match counts if the filter is selected.
     * @returns {number} The number of matching items across all selected filter models.
     * @private
     */
    _getMatchCount() {
        let count = 0;

        each(this.props.facetModel.getFilterModels(), function (filter) {
            // If the isAllFiltersActive flag is set then include all filter counts. Otherwise, just count the selected ones.
            if (this.props.facetModel.isAllFiltersActive() || filter.isSelected()) {
                count = count + filter.getMatchCount();
            }
        }, this);
        return (count);
    }

    /**
     * Converts minutes to a formatted time string (e.g. 1290 becomes 9:30pm).
     */
    _minutesToTime(minutes) {
        let midnight = moment.utc(0);
        let minutesAfterMidnight = midnight.add(minutes, 'minutes');
        return (minutesAfterMidnight.format("h:mma"));
    }

    /*
     * Converts a raw time value (e.g. 730 or 2130) to the number of minutes
     * past midnight (450 or 1290).
     */
    _timeToMinutes(time) {
        let paddedTime = time.length === 3 ? `0${time}` : time;
        let hours = paddedTime.substring(0, 2);
        let minutes = paddedTime.substring(2);
        let t = moment.utc(0).add({hours: hours, minutes: minutes});
        return (t.hours() * 60) + t.minutes();
    }

    /**
     * Called each time the slider handles change position.
     */
    onChange = (values) => this.setState({currentStartMinutes: parseInt(values[0]), currentEndMinutes: parseInt(values[1])});

    /**
     * Called after the slider handles come to rest.
     */
    onAfterChange = (values) => {
        /*
         * Iterate through the filter models. If the range value in the filter falls within
         * the current range of the slider then add the filter Id to the filter list so
         * that it can be selected (and the missing ids can be unselected).
         */
        let filterIds = [];
        each(this.props.facetModel.getFilterModels(), function (filter) {
            let filterId = filter.id;
            //  TBAs always match. The key would be "TBA" instead of "123:456"
            if (! filter.key.includes(":")) {
                filterIds.push(filterId);
            } else {
                let timeTokens = filter.key.split(":");

                let start = this._timeToMinutes(timeTokens[0]);
                let end = this._timeToMinutes(timeTokens[1]);

                let csm = this.state.currentStartMinutes;
                let cem = this.state.currentEndMinutes;

                if (start >= csm && end <= cem) {
                    filterIds.push(filterId);
                }
            }
        }, this);

        //  Call the filter change callback ...
        this.props.handleFilterChange(filterIds);
    };

    render() {
        //  If the match count is zero then don't display the zero.
        let count = <span>&nbsp;</span>;
        let matchCount = this._getMatchCount();
        if (matchCount > 0) {
            count = `(${matchCount}})`;
        }

        let countComponent =
            <div className="count"
                 aria-label={'Filter LABEL' + ' contains ' + matchCount + ' result' + (matchCount !== 1 ? 's' : '')}>
                {count}
            </div>;

        return (
            <div className="slider-container">
                <Row>
                    {/* className={this.props.disabled ? "disabled text-center slider-label" : "text-center slider-label"} */}
                    <Col xs={6}>
                        {this._minutesToTime(this.state.currentStartMinutes)}
                    </Col>
                    <Col xs={6} className="text-end">
                        {this._minutesToTime(this.state.currentEndMinutes)}
                    </Col>
                    <Col xs={12} style={{padding: '15px 22px'}}>
                        <SliderRange
                            min={this.state.minStartMinutes}
                            max={this.state.maxEndMinutes}
                            step={10}
                            value={[parseInt(this.state.currentStartMinutes), parseInt(this.state.currentEndMinutes)]}
                            onChange={this.onChange}
                            onAfterChange={this.onAfterChange}
                            disabled={this.props.disabled}
                            handleFormatter={this._minutesToTime}/>
                    </Col>
                    <Col xs={12}>
                        {countComponent}
                    </Col>
                </Row>
            </div>
        );
    }
}

TimeRangeFilter.propTypes = {
    handleFilterChange: PropTypes.func.isRequired,
    facetModel: PropTypes.object.isRequired
};

export default TimeRangeFilter;